import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { getDownloadURL } from "firebase/storage";
import { useDispatch } from "react-redux";
import { notifyError } from "redux/actions/Notifications";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import { sanitizeFileName } from "utils/files";
import ImageCropModal from "./ImageCropModal";
import { IMAGE_DISCLAIMER, IMAGE_SOURCE_MAX_BYTES, getCroppedBannerFile, readFileAsDataUrl } from "../utils";

/**
 * Single-image uploader for a campaign asset. The user picks any image; a 3:1
 * cropper opens and, on apply, the image is cropped/compressed to a <=1MB banner
 * before being uploaded to Firebase. Shows a 3:1 banner preview matching how it
 * is rendered in the Client Portal.
 */
function AssetImageUploader({ value, onChange, campaignId, Storage, disabled }) {
    const dispatch = useDispatch();
    const inputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [cropOpen, setCropOpen] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [baseName, setBaseName] = useState("banner");
    // Storage paths uploaded during this session, so we can clean them up on
    // replace/remove without touching images referenced elsewhere.
    const uploadedPathRef = useRef(null);

    const openFilePicker = () => {
        if (disabled || isUploading) return;
        inputRef.current?.click();
    };

    const deleteSessionUpload = async () => {
        if (!uploadedPathRef.current) return;
        try {
            await Storage.deleteAsync({ path: uploadedPathRef.current });
        } catch (error) {
            // Best-effort cleanup; ignore failures.
        } finally {
            uploadedPathRef.current = null;
        }
    };

    const onFileSelected = async (event) => {
        const file = event.target.files?.[0];
        // Allow re-selecting the same file later.
        event.target.value = "";
        if (!file) return;

        if (!file.type || !file.type.startsWith("image/")) {
            dispatch(notifyError("Only image files are allowed."));
            return;
        }
        if (file.size > IMAGE_SOURCE_MAX_BYTES) {
            dispatch(notifyError("The image is too large. Please choose one under 25MB."));
            return;
        }

        try {
            const dataUrl = await readFileAsDataUrl(file);
            const extension = `.${file.name.split(".").pop()}`;
            setBaseName(sanitizeFileName(file.name.replace(new RegExp(`${extension}$`), "")) || "banner");
            setCropImageSrc(dataUrl);
            setCropOpen(true);
        } catch (error) {
            dispatch(notifyError(error.message || "Could not read the selected image."));
        }
    };

    const onCancelCrop = () => {
        if (isUploading) return;
        setCropOpen(false);
        setCropImageSrc(null);
    };

    const onApplyCrop = async (croppedAreaPixels) => {
        if (!croppedAreaPixels) return;
        try {
            setIsUploading(true);
            const file = await getCroppedBannerFile(cropImageSrc, croppedAreaPixels, baseName);
            await deleteSessionUpload();
            const path = `campaigns/${campaignId}/assets/${Date.now()}_${file.name}`;
            const snap = await Storage.uploadAsync({ path, file });
            const url = await getDownloadURL(snap.ref);
            uploadedPathRef.current = path;
            onChange(url);
            setCropOpen(false);
            setCropImageSrc(null);
        } catch (error) {
            dispatch(notifyError(error.message || "Error processing the image."));
        } finally {
            setIsUploading(false);
        }
    };

    const onRemove = async () => {
        if (disabled || isUploading) return;
        await deleteSessionUpload();
        onChange("");
    };

    return (
        <div className="asset-image-uploader">
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={onFileSelected}
                disabled={disabled || isUploading}
            />

            <ImageCropModal
                open={cropOpen}
                imageSrc={cropImageSrc}
                onCancel={onCancelCrop}
                onApply={onApplyCrop}
                isProcessing={isUploading}
            />

            {value ? (
                <div className="asset-image-uploader__preview">
                    <div className="banner-3-1" style={{ backgroundImage: `url("${value}")` }} />
                    {!disabled && (
                        <div className="asset-image-uploader__actions">
                            <Button secondary small outline onClick={openFilePicker} loading={isUploading}>
                                <Icon name="upload" size="sm" color="secondary" />
                                <span>Replace</span>
                            </Button>
                            <Button secondary small link onClick={onRemove}>
                                <Icon name="delete" size="sm" color="secondary" />
                                <span>Remove</span>
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                !disabled && (
                    <div className="asset-image-uploader__empty" onClick={openFilePicker}>
                        {isUploading ? (
                            <div className="icon icon--loading loading" />
                        ) : (
                            <>
                                <Icon name="add-photo" size="xlg" color="secondary" />
                                <span className="asset-image-uploader__cta">Click to add an image *</span>
                            </>
                        )}
                    </div>
                )
            )}

            {!disabled && <p className="asset-image-uploader__disclaimer">{IMAGE_DISCLAIMER}</p>}
        </div>
    );
}

AssetImageUploader.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    Storage: PropTypes.shape({
        uploadAsync: PropTypes.func.isRequired,
        deleteAsync: PropTypes.func.isRequired,
    }).isRequired,
    disabled: PropTypes.bool,
};

AssetImageUploader.defaultProps = {
    value: "",
    disabled: false,
};

export default AssetImageUploader;
